import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import tableSplitService from "../services/tableSplitService";

export const useTableSplits = () => {
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- Fetch all splits for a specific table ---
  const fetchSplits = async (tableId) => {
  try {
    const response = await tableSplitService.getSplitsByTable(tableId);
    if (response) {
      setSplits(response);
      return response; // ✅ ADD THIS LINE
    }
    return [];
  } catch (error) {
    console.error("❌ Failed to fetch splits:", error);
    return [];
  }
};


  // --- Helper: Generate next available split name (A, B, C, etc.) ---
  const generateNextSplitName = (existingSplits) => {
    const existingNames = existingSplits.map((s) => s.splitName);
    let letterCode = 65; // ASCII for 'A'
    while (existingNames.includes(`${String.fromCharCode(letterCode)}`)) {
      letterCode++;
    }
    return `${String.fromCharCode(letterCode)}`;
  };

  // --- Create a new split for a table ---
  const createSplitForTable = useCallback(
    async (tableId, data = {}) => {
      try {
        setLoading(true);

        // Counter mode: let backend handle cleanup + next split name
        if (data.isCounterMode) {
          const payload = {
            tableId: null, // backend resolves from MAVERICK_COUNTER
            isCounterMode: true,
            peopleCount: data.peopleCount || 1,
            status: "active",
          };
          const newSplit = await tableSplitService.createSplit(payload);
          await fetchSplits(newSplit.tableId || tableId);
          return newSplit;
        }

        // Step 1: Get current splits for that table
        const existingSplits = await fetchSplits(tableId);

        // Step 2: Find next available split name
        const nextSplitName = generateNextSplitName(existingSplits);

        // Step 3: Build payload
        const payload = {
          tableId,
          splitName: nextSplitName,
          peopleCount: data.peopleCount || 1,
          status: "active",
        };

        // Step 4: Call API
        const newSplit = await tableSplitService.createSplit(payload);

        toast.success(`${nextSplitName} created successfully`);
        await fetchSplits(tableId);

        return newSplit;
      } catch (error) {
        console.error("❌ Failed to create split:", error);
        toast.error("Could not create split");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchSplits]
  );

  // --- Update split status ---
  const updateSplitStatus = useCallback(
    async (splitId, status, tableId) => {
      try {
        await tableSplitService.updateSplitStatus(splitId, status);
        toast.success("Split status updated");
        if (tableId) await fetchSplits(tableId);
      } catch (error) {
        console.error("❌ Failed to update split status:", error);
        toast.error("Could not update split status");
      }
    },
    [fetchSplits]
  );

  // --- Update people count for a split ---
  const updatePeopleCount = useCallback(
  async (tableId, splitObj, newCount) => {
    try {
      const payload = {
        ...splitObj,
        peopleCount: newCount, // backend expects this
      };

      await tableSplitService.updateSplit(tableId, splitObj.splitName, payload);

      //toast.success("People count updated");
      await fetchSplits(tableId);
    } catch (error) {
      console.error("❌ Failed to update people count:", error);
      toast.error("Could not update people count");
    }
  },
  [fetchSplits]
);


  // --- Delete a specific split ---
  const deleteSplit = useCallback(
    async (splitId, tableId) => {
      try {
        await tableSplitService.deleteSplit(splitId);
        toast.success("Split deleted");
        if (tableId) await fetchSplits(tableId);
      } catch (error) {
        console.error("❌ Failed to delete split:", error);
        toast.error("Could not delete split");
      }
    },
    [fetchSplits]
  );

  // --- Clear all splits for a specific table ---
  const clearTableSplits = useCallback(
    async (tableId) => {
      try {
        await tableSplitService.clearTableSplits(tableId);
        toast.success("All splits cleared");
        await fetchSplits(tableId);
      } catch (error) {
        console.error("❌ Failed to clear table splits:", error);
        toast.error("Could not clear table splits");
      }
    },
    [fetchSplits]
  );

  // --- Fetch all splits globally (optional) ---
  const fetchAllSplits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tableSplitService.getAllSplits();
      setSplits(response?.data || []);
    } catch (error) {
      console.error("❌ Failed to fetch all splits:", error);
      toast.error("Failed to load all splits");
    } finally {
      setLoading(false);
    }
  }, []);

  // --- Fetch single split by ID ---
  const fetchSplitById = useCallback(async (splitId) => {
    try {
      const response = await tableSplitService.getSplitById(splitId);
      return response?.data;
    } catch (error) {
      console.error("❌ Failed to fetch split by ID:", error);
      toast.error("Could not fetch split details");
    }
  }, []);

  return {
    splits,
    loading,
    fetchSplits,
    fetchAllSplits,
    fetchSplitById,
    createSplitForTable,
    updateSplitStatus,
    updatePeopleCount,
    deleteSplit,
    clearTableSplits,
  };
};
